import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import data from "layouts/dashboard/components/Projects/data";
import NewDevice from "./newDevice";

const filterFieldProps = {
  fullWidth: true,
  size: "small",
  variant: "outlined",
  InputProps: { style: { fontSize: "0.8rem" } },
  InputLabelProps: { style: { fontSize: "0.8rem" } },
  sx: { "& .MuiOutlinedInput-root": { height: "38px" }, "& .MuiSelect-select": { fontSize: "0.8rem" } },
};

function Devices() {
  const { t } = useTranslation();
  const { deviceTableColumns } = data();
  const api = useApi();
  const [rawDevices, setRawDevices] = useState([]);
  const [devices, setDevices] = useState([]);
  const [openConfigurator, setOpenConfigurator] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState({});

  // Filter state
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMapping, setFilterMapping] = useState("");

  const getDevices = () => {
    api.get("device/browse")
      .then((response) => {
        setRawDevices(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error(t("no_permission_message"), { id: "DevicesPermissionError" });
          return;
        }
        toast.error(t("devicePage.failedToLoad"), { id: "DevicesFetchError" });
      });
  };

  useEffect(() => {
    getDevices();
  }, []);

  function editDevice(item) {
    setSelectedDevice(item);
    setOpenConfigurator(true);
  }

  function addNewDevice() {
    setSelectedDevice({});
    setOpenConfigurator(true);
  }

  useEffect(() => {
    let result = [...rawDevices];
    const search = filterSearch.trim().toLowerCase();
    if (search) {
      result = result.filter((d) =>
        [d.serialNumber, d.deviceName, d.deviceType, d.mid, d.tid].some((v) => v?.toLowerCase().includes(search)));
    }
    if (filterStatus) {
      result = result.filter((d) => d.status === filterStatus);
    }
    if (filterMapping) {
      result = result.filter((d) => String(!!d.mapped) === filterMapping);
    }
    setDevices(result.map((item) => ({
      ...item,
      action: (
        <MDBox display="flex" alignItems="center" lineHeight={1}>
          <Icon fontSize="small" style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); editDevice(item); }}>create</Icon>
        </MDBox>
      ),
    })));
  }, [rawDevices, filterSearch, filterStatus, filterMapping]);

  const resetFilters = () => {
    setFilterSearch("");
    setFilterStatus("");
    setFilterMapping("");
  };

  return (
    <DashboardLayout>
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Grid container justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="h5" fontWeight="medium">
                  {t("devicePage.title")}
                </MDTypography>
                <MDTypography variant="button" fontWeight="regular" color="secondary">
                  {devices.length} {t("devicePage.title")}
                </MDTypography>
              </MDBox>
              <MDButton variant="gradient" color="info" onClick={addNewDevice}>
                {t("devicePage.addDevice")}
              </MDButton>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={12}>
            <MDBox sx={(theme) => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: "8px", background: theme.palette.white.main, overflow: "hidden" })}>
              {/* Filter Panel */}
              <MDBox px={2} py={1.5} sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={true}>
                    <TextField
                      label={t("devicePage.search")}
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                      {...filterFieldProps}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={true}>
                    <TextField select label={t("deviceTableColumns.status")} value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)} {...filterFieldProps}>
                      <MenuItem value="" style={{ fontSize: "0.8rem" }}>{t("all")}</MenuItem>
                      <MenuItem value="ACTIVE" style={{ fontSize: "0.8rem" }}>{t("active")}</MenuItem>
                      <MenuItem value="INACTIVE" style={{ fontSize: "0.8rem" }}>{t("inactive")}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={true}>
                    <TextField select label={t("deviceTableColumns.mapping")} value={filterMapping}
                      onChange={(e) => setFilterMapping(e.target.value)} {...filterFieldProps}>
                      <MenuItem value="" style={{ fontSize: "0.8rem" }}>{t("all")}</MenuItem>
                      <MenuItem value="true" style={{ fontSize: "0.8rem" }}>{t("devicePage.mapped")}</MenuItem>
                      <MenuItem value="false" style={{ fontSize: "0.8rem" }}>{t("devicePage.notMapped")}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2} lg="auto">
                    <MDButton variant="outlined" color="secondary" onClick={resetFilters} size="small" style={{ fontSize: "0.75rem", padding: "5px 16px", whiteSpace: "nowrap" }}>
                      {t("reset")}
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>
              <DataTable
                table={{ columns: deviceTableColumns, rows: devices }}
                isSorted={true}
                showTotalEntries={true}
                entriesPerPage={true}
                entriesPerPagePosition="bottom"
                noEndBorder
                canSearch={false}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <NewDevice
        openConfigurator={openConfigurator}
        setOpenConfigurator={setOpenConfigurator}
        device={selectedDevice}
        onSaved={getDevices}
      />
      <Footer />
    </DashboardLayout>
  );
}

export default Devices;
